import type { TokenInfo, TokenList } from '@uniswap/token-lists'

const ADDRESS_PATTERN = /^0x[a-fA-F0-9]{40}$/

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

function validateString(value: unknown, path: string, { allowEmpty = false, maxLength = Infinity } = {}): string {
  if (typeof value !== 'string') {
    throw new Error(`${path} must be a string`)
  }
  if (!allowEmpty && value.length === 0) {
    throw new Error(`${path} must not be empty`)
  }
  if (value.length > maxLength) {
    throw new Error(`${path} must not be longer than ${maxLength} characters`)
  }
  return value
}

function validateInteger(value: unknown, path: string, { min = Number.MIN_SAFE_INTEGER, max = Number.MAX_SAFE_INTEGER } = {}) {
  if (!Number.isInteger(value) || (value as number) < min || (value as number) > max) {
    throw new Error(`${path} must be an integer between ${min} and ${max}`)
  }
  return value as number
}

function validateUrl(value: unknown, path: string): string | undefined {
  if (value === undefined) return undefined
  const url = validateString(value, path)

  try {
    const parsed = new URL(url)
    if (parsed.protocol === 'http:' || parsed.protocol === 'https:' || parsed.protocol === 'ipfs:') {
      return url
    }
  } catch {
    // handled below
  }

  throw new Error(`${path} must be an http, https, or ipfs URL`)
}

function validateVersion(value: unknown): TokenList['version'] {
  if (!isRecord(value)) {
    throw new Error('version must be an object')
  }

  return {
    major: validateInteger(value.major, 'version.major', { min: 0 }),
    minor: validateInteger(value.minor, 'version.minor', { min: 0 }),
    patch: validateInteger(value.patch, 'version.patch', { min: 0 }),
  }
}

function validateTokenInfo(value: unknown, index: number): TokenInfo {
  const path = `tokens[${index}]`
  if (!isRecord(value)) {
    throw new Error(`${path} must be an object`)
  }

  const address = validateString(value.address, `${path}.address`)
  if (!ADDRESS_PATTERN.test(address)) {
    throw new Error(`${path}.address must be a 0x-prefixed address`)
  }

  const logoURI = validateUrl(value.logoURI, `${path}.logoURI`)
  const tags =
    value.tags === undefined
      ? undefined
      : (() => {
          if (!Array.isArray(value.tags)) {
            throw new Error(`${path}.tags must be an array`)
          }
          return value.tags.map((tag, tagIndex) =>
            validateString(tag, `${path}.tags[${tagIndex}]`, { maxLength: 40 })
          )
        })()
  const extensions =
    value.extensions === undefined
      ? undefined
      : (() => {
          if (!isRecord(value.extensions)) {
            throw new Error(`${path}.extensions must be an object`)
          }
          return value.extensions
        })()

  return {
    chainId: validateInteger(value.chainId, `${path}.chainId`, { min: 1 }),
    address,
    decimals: validateInteger(value.decimals, `${path}.decimals`, { min: 0, max: 255 }),
    name: validateString(value.name, `${path}.name`, { allowEmpty: true, maxLength: 80 }),
    symbol: validateString(value.symbol, `${path}.symbol`, { allowEmpty: true, maxLength: 40 }),
    ...(logoURI ? { logoURI } : {}),
    ...(tags ? { tags } : {}),
    ...(extensions ? { extensions } : {}),
  } as TokenInfo
}

function validateTokenArray(json: unknown): TokenInfo[] {
  if (!Array.isArray(json)) {
    throw new Error('tokens must be an array')
  }
  if (json.length === 0) {
    throw new Error('tokens must not be empty')
  }
  return json.map(validateTokenInfo)
}

/**
 * Validates an array of tokens.
 * @param json the TokenInfo[] to validate
 */
export async function validateTokens(json: TokenInfo[]): Promise<TokenInfo[]> {
  try {
    return validateTokenArray(json)
  } catch (error) {
    throw new Error(`Tokens failed validation: ${error.message}`)
  }
}

/**
 * Validates a token list.
 * @param json the TokenList to validate
 */
export async function validateTokenList(json: TokenList): Promise<TokenList> {
  try {
    if (!isRecord(json)) {
      throw new Error('Token list must be an object')
    }

    const logoURI = validateUrl(json.logoURI, 'logoURI')
    const keywords =
      json.keywords === undefined
        ? undefined
        : (() => {
            if (!Array.isArray(json.keywords)) {
              throw new Error('keywords must be an array')
            }
            return json.keywords.map((keyword, index) =>
              validateString(keyword, `keywords[${index}]`, { maxLength: 40 })
            )
          })()
    const tags =
      json.tags === undefined
        ? undefined
        : (() => {
            if (!isRecord(json.tags)) {
              throw new Error('tags must be an object')
            }
            return json.tags as TokenList['tags']
          })()

    return {
      name: validateString(json.name, 'name', { maxLength: 80 }),
      timestamp: validateString(json.timestamp, 'timestamp'),
      version: validateVersion(json.version),
      tokens: validateTokenArray(json.tokens),
      ...(logoURI ? { logoURI } : {}),
      ...(keywords ? { keywords } : {}),
      ...(tags ? { tags } : {}),
    } as TokenList
  } catch (error) {
    throw new Error(`Token list failed validation: ${error.message}`)
  }
}
